import React, { type RefObject, useCallback } from 'react';

import { fg } from '@atlaskit/platform-feature-flags/fg';
import type { DialogContentProps, PopupProps } from '@atlaskit/popup/types';
import { applyInitialFocus } from '@atlaskit/top-layer/apply-initial-focus';
import { getFirstFocusable } from '@atlaskit/top-layer/get-first-focusable';

import { FocusOnLoad, type FocusOnLoadProps } from './focus-on-load';
import { shouldMoveFocus } from './should-move-focus';

type FocusInsideOnLoadProps = Omit<FocusOnLoadProps, 'onLoad'> & {
	initialFocusRef: RefObject<HTMLElement | null>;
	role?: PopupProps['role'];
	shouldTransferFocusRef: RefObject<boolean>;
	triggerRef: RefObject<HTMLElement | null>;
	/**
	 * Ref holding the popup's own initial focus setter, taken from the `content`
	 * render props. It is a ref because the content element is created before
	 * the render props are available.
	 */
	setInitialFocusRef: RefObject<DialogContentProps['setInitialFocusRef'] | null>;
};
/**
 * Moves focus into the popup once the entry point has finished loading, so
 * makers don't have to manage focus manually after the loading fallback is
 * replaced by the final experience.
 */
export function FocusInsideOnLoad({
	entryPointReference,
	initialFocusRef,
	role,
	setInitialFocusRef,
	shouldTransferFocusRef,
	triggerRef,
	waitForContentReady,
}: FocusInsideOnLoadProps): React.JSX.Element {
	const handleLoad = useCallback(
		(container: HTMLElement) => {
			if (fg('platform_dst-automated_focus')) {
				const initialFocusTarget = initialFocusRef.current;
				const hasInitialFocusTarget =
					initialFocusTarget !== null && container.contains(initialFocusTarget);

				if (
					!hasInitialFocusTarget &&
					!shouldMoveFocus({ container, role, shouldTransferFocusRef, triggerRef })
				) {
					return;
				}

				if (hasInitialFocusTarget) {
					// An entry point explicitly selected this target. Unlike the role-based
					// defaults below, it must take precedence even for roles such as
					// `tooltip` that do not otherwise move focus.
					initialFocusTarget.focus();
					return;
				}

				const result = applyInitialFocus({ container, role });

				if (!result.applied) {
					container.tabIndex = -1;
					applyInitialFocus({
						container,
						initialFocusRef: { current: container },
						role,
					});
				}
				return;
			}
			// Focus the first focusable element of the loaded experience, or the
			// popup itself when the experience has nothing focusable in it.
			const target = getFirstFocusable({ container }) ?? container;

			// The popup focuses its initial target once, when it opens. If that has
			// not happened yet — because the entry point was already loaded — this
			// makes the popup focus the target instead of itself, avoiding focus
			// moving twice.
			setInitialFocusRef.current?.(target);
			target.focus();
		},
		[initialFocusRef, role, setInitialFocusRef, shouldTransferFocusRef, triggerRef],
	);

	return (
		<FocusOnLoad
			entryPointReference={entryPointReference}
			onLoad={handleLoad}
			waitForContentReady={waitForContentReady}
		/>
	);
}
